import { makeStyles } from '@material-ui/core/styles'
import {
  CARD_BORDER,
  CARD_SHADOW,
  MUTED_TEXT,
  PAGE_BACKGROUND,
  SOFT_PANEL_BACKGROUND,
  SUMMARY_WIDTH,
} from 'pages/things-to-do/events/register/registration-with-payment-intents/redesign/styles'

// This step shares its tokens with the redesigned group event registration
// (neutrals, card border and shadow, muted text, summary width) and takes
// colors, fonts and buttons from the `offeringsRefresh` theme like it does.
// Focus rings, card radius, fact rows, dividers and the pill colors below
// mirror that sheet.
export { CARD_BORDER, MUTED_TEXT, PAGE_BACKGROUND, SUMMARY_WIDTH }

const card = theme => ({
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: 24,
  boxShadow: CARD_SHADOW,
  [theme.breakpoints.down('xs')]: {
    borderRadius: 20,
  },
})

const focusRing = theme => ({
  outline: `2px solid ${theme.palette.primary.lightest}`,
  outlineOffset: 2,
})

const useStyles = makeStyles(theme => ({
  page: {
    // The nested theme's body typography still resolves to the base theme's
    // Inter, so set the refresh font here and let plain text inherit it.
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
  },
  inner: {
    maxWidth: 1080,
    margin: '0 auto',
    padding: theme.spacing(1, 4, 8),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 3),
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: `minmax(0, 1fr) ${SUMMARY_WIDTH}px`,
    gap: theme.spacing(4),
    alignItems: 'start',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: theme.spacing(2),
    },
  },
  mainCol: { minWidth: 0 },
  sideCol: {
    position: 'sticky',
    top: theme.spacing(3),
  },

  // Mobile top card
  mobileTopCard: {
    ...card(theme),
    marginBottom: theme.spacing(2.5),
    overflow: 'hidden',
  },
  mobileBackRow: {
    padding: theme.spacing(0, 1.5),
    borderBottom: `1px solid ${CARD_BORDER}`,
    '& a': {
      margin: 0,
      padding: theme.spacing(1.5, 0.5),
      minHeight: 48,
      fontSize: 16,
    },
  },
  mobileSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
  },
  mobileSummaryText: { flex: 1, minWidth: 0 },
  mobileSummaryTitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  mobileSummarySub: {
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 13,
    lineHeight: 1.4,
  },
  mobilePrice: {
    flexShrink: 0,
    fontSize: 14,
    fontWeight: 700,
  },
  desktopBack: {
    marginBottom: theme.spacing(1),
    '& a': {
      margin: 0,
      marginTop: theme.spacing(2),
      fontSize: 16,
    },
  },

  // Heading (same scale as the group event registration title)
  heading: {
    margin: 0,
    fontSize: 40,
    lineHeight: 1.1,
    [theme.breakpoints.down('xs')]: {
      fontSize: 30,
    },
  },
  subtitle: {
    margin: theme.spacing(1, 0, 0),
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.5,
  },

  // Date strip
  stripRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: theme.spacing(3, 0),
  },
  roundButton: {
    boxSizing: 'border-box',
    flexShrink: 0,
    width: 36,
    height: 36,
    padding: 0,
    borderRadius: '50%',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${CARD_BORDER}`,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': { backgroundColor: SOFT_PANEL_BACKGROUND },
    '&:focus': { outline: 'none' },
    '&:focus-visible': focusRing(theme),
    '& svg': { fontSize: 18 },
    '&$roundButtonDisabled': {
      cursor: 'default',
      color: theme.palette.grey.main,
      backgroundColor: theme.palette.common.white,
    },
  },
  roundButtonDisabled: {},
  calendarButton: {
    width: 40,
    height: 40,
  },
  strip: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
    display: 'flex',
    gap: theme.spacing(1),
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    // Room for the focus outline, which the scroller would otherwise clip.
    padding: '4px',
    margin: '-4px',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  dayCard: {
    flex: '0 0 72px',
    width: 72,
    height: 68,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    padding: 0,
    borderRadius: 16,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${CARD_BORDER}`,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily,
    transition: 'background-color 120ms, border-color 120ms',
    '&:hover': { borderColor: theme.palette.primary.main },
    '&:focus': { outline: 'none' },
    '&:focus-visible': focusRing(theme),
  },
  dayCardSelected: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  dayCardClosed: {
    backgroundColor: SOFT_PANEL_BACKGROUND,
    color: theme.palette.grey.main,
    cursor: 'default',
    '&:hover': { borderColor: CARD_BORDER },
  },
  dayLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    lineHeight: '14px',
  },
  dayNumber: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: '24px',
  },
  dayCaption: {
    fontSize: 11,
    fontWeight: 400,
    lineHeight: '14px',
  },

  // Calendar popover
  calendarPaper: {
    borderRadius: 16,
    border: `1px solid ${CARD_BORDER}`,
    boxShadow: CARD_SHADOW,
    padding: theme.spacing(0.5, 0, 1),
  },

  // Time card
  timeCard: {
    ...card(theme),
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2.5),
      borderRadius: 20,
    },
  },
  timeCardTitle: {
    margin: 0,
    fontSize: 22,
    lineHeight: 1.25,
  },
  dayPart: { marginTop: theme.spacing(2.5) },
  dayPartHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    margin: theme.spacing(0, 0, 1.25),
    color: MUTED_TEXT,
    fontFamily: theme.typography.fontFamily,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    lineHeight: '16px',
    textTransform: 'uppercase',
    '& svg': { fontSize: 16 },
  },
  slotGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    },
  },
  slot: {
    height: 40,
    padding: theme.spacing(0, 1),
    borderRadius: 20,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${CARD_BORDER}`,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    transition: 'background-color 120ms, border-color 120ms',
    '&:hover': { borderColor: theme.palette.primary.main },
    '&:focus': { outline: 'none' },
    '&:focus-visible': focusRing(theme),
  },
  slotSelected: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': { borderColor: theme.palette.primary.main },
  },
  noTimes: { marginTop: theme.spacing(3) },

  // Bottom bar
  bottomBar: {
    ...card(theme),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(1.5, 2, 1.5, 3),
    [theme.breakpoints.down('sm')]: {
      position: 'sticky',
      bottom: theme.spacing(1.5),
      zIndex: 2,
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 1.5, 1.5, 2.5),
    },
  },
  bottomText: {
    margin: 0,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.5,
  },
  bottomTextSelected: {
    color: theme.palette.text.primary,
    fontWeight: 700,
  },
  // Color, radius, height, font and disabled styling come from the theme's
  // contained primary button, like the group event registration CTAs.
  continueButton: {
    flexShrink: 0,
  },

  // Summary card (mirrors the group event summary card)
  summaryCard: {
    ...card(theme),
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  summaryDivider: {
    height: 1,
    margin: theme.spacing(2.5, 0),
    border: 0,
    backgroundColor: CARD_BORDER,
  },
  coachRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  avatar: {
    width: 48,
    height: 48,
    flexShrink: 0,
    border: `2px solid ${theme.palette.secondary.light}`,
    backgroundColor: theme.palette.grey.light,
    color: theme.palette.common.white,
    fontSize: 18,
  },
  avatarSmall: {
    width: 40,
    height: 40,
    fontSize: 16,
  },
  coachName: {
    margin: 0,
    fontSize: 17,
    lineHeight: 1.25,
  },
  coachTitle: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5, 1),
    marginTop: theme.spacing(0.5),
    color: MUTED_TEXT,
    fontSize: 12,
    lineHeight: 1.4,
  },
  memberChip: {
    height: 24,
    backgroundColor: '#E9ECF3',
    color: theme.palette.primary.main,
    fontSize: 13,
    fontWeight: 700,
  },
  lessonTitle: {
    margin: theme.spacing(0, 0, 2),
    fontSize: 24,
    lineHeight: 1.2,
  },
  facts: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  fact: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(1.25),
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.45,
    '& svg': {
      flexShrink: 0,
      marginTop: 1,
      color: theme.palette.secondary.main,
      fontSize: 18,
    },
  },
  totalRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: 700,
  },
  totalValue: {
    fontSize: 28,
    lineHeight: 1.1,
    [theme.breakpoints.down('xs')]: {
      fontSize: 22,
    },
  },

  // Coach-only add custom time
  customTime: {
    marginTop: theme.spacing(3),
    '& p': { margin: 0, color: MUTED_TEXT, fontSize: 14 },
    '& button': { marginLeft: theme.spacing(-1) },
  },
}))

export default useStyles
